import { supabase } from "@/integrations/supabase/client"

// Database types
export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  role: 'admin' | 'clinician' | 'physician' | 'director' | 'supervisor' | 'technician'
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Patient {
  id: string
  mrn: string
  first_name: string
  last_name: string
  middle_name?: string
  date_of_birth: string
  gender: 'male' | 'female' | 'other'
  weight_kg?: number
  abo_rh?: string
  ssn?: string
  physician?: string
  phone?: string
  email?: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  emergency_contact_name?: string
  emergency_contact_phone?: string
  insurance_info?: string
  medical_history?: string
  allergies?: string
  current_medications?: string
  created_at: string
  updated_at: string
}

export interface Order {
  id: string
  patient_id: string
  creator_id: string
  order_date: string
  order_status: 'unapproved' | 'new_order_placed' | 'new_order_received' | 'results_final' | 'infusion_ordered' | 'infusion_shipped' | 'infusion_completed' | 'cancelled'
  diagnosis?: string
  treatment_type?: string
  cell_dose?: number
  cell_dose_unit?: string
  infusion_date?: string
  special_instructions?: string
  physician_notes?: string
  approved_by?: string
  approved_at?: string
  created_at: string
  updated_at: string
  // Relations
  patients?: Patient
  creator?: User
  approver?: User
  units?: Unit[]
}

export interface Unit {
  id: string
  order_id: string
  unit_identifier: string
  collection_date?: string
  received_date?: string
  volume_ml?: number
  total_nucleated_cells?: number
  cd34_positive_cells?: number
  viability_percentage?: number
  unit_status: 'ordered' | 'shipped' | 'infused' | 'available'
  storage_location?: string
  notes?: string
  created_at: string
  updated_at: string
}

export interface LoginAttempt {
  id: string
  email: string
  ip_address?: string
  success: boolean
  attempted_at: string
}

// Authentication helper functions
export const auth = {
  async login(email: string, password: string) {
    try {
      // Call the secure database function for authentication
      const { data, error } = await supabase
        .rpc('authenticate_user', {
          user_email: email,
          user_password: password
        });

      if (error) {
        throw new Error('Authentication service error');
      }

      // Cast the data to the expected structure
      const authResult = (data as unknown) as {
        success: boolean;
        user?: User;
        error?: string;
        require_captcha?: boolean;
      };

      if (!authResult.success) {
        throw new Error(authResult.error || 'Invalid email or password');
      }

      return {
        user: authResult.user as User, 
        requireCaptcha: authResult.require_captcha || false 
      };
    } catch (error) {
      console.error('Auth: Login error:', error);
      throw error;
    }
  },

  async logout() {
    // Clear any session data
    localStorage.removeItem('cellorder_user')
    return true
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('cellorder_user')
    return userStr ? JSON.parse(userStr) : null
  },

  setCurrentUser(user: User) {
    localStorage.setItem('cellorder_user', JSON.stringify(user))
  }
}

// Data access functions
export const api = {
  // Orders
  async getOrders(filters?: {
    mrn?: string
    lastName?: string
    firstName?: string
    status?: string
    department?: string
    dateFrom?: string
    dateTo?: string
  }) {
    let query = supabase
      .from('orders')
      .select(`
        *,
        patients(id, mrn, first_name, last_name, middle_name, date_of_birth, gender, weight_kg, abo_rh, physician, emergency_contact_name, emergency_contact_phone, ssn)
      `)
      .order('order_date', { ascending: false })

    // For MRN, lastName, and firstName filters, we need to handle them differently
    // to ensure proper joins
    if (filters?.mrn || filters?.lastName || filters?.firstName) {
      // Get patient IDs that match the criteria first
      let patientQuery = supabase
        .from('patients')
        .select('id')

      if (filters?.mrn) {
        patientQuery = patientQuery.ilike('mrn', `%${filters.mrn}%`)
      }
      if (filters?.lastName) {
        patientQuery = patientQuery.ilike('last_name', `%${filters.lastName}%`)
      }
      if (filters?.firstName) {
        patientQuery = patientQuery.ilike('first_name', `%${filters.firstName}%`)
      }

      const { data: matchingPatients } = await patientQuery
      
      if (matchingPatients && matchingPatients.length > 0) {
        const patientIds = matchingPatients.map(p => p.id)
        query = query.in('patient_id', patientIds)
      } else {
        // No matching patients found, return empty result
        return []
      }
    }

    // Apply other filters
    if (filters?.status && filters.status !== "" && ['unapproved', 'new_order_placed', 'new_order_received', 'results_final', 'infusion_ordered', 'infusion_shipped', 'infusion_completed', 'cancelled'].includes(filters.status)) {
      query = query.eq('order_status', filters.status as Order['order_status'])
    }
    if (filters?.dateFrom) {
      query = query.gte('order_date', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('order_date', filters.dateTo)
    }

    const { data, error } = await query

    if (error) {
      throw error;
    }
    
    return data as any[] || []
  },

  async getUnapprovedOrders() {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        patients(*),
        creator:users!orders_creator_id_fkey(*)
      `)
      .eq('order_status', 'unapproved')
      .order('order_date', { ascending: false })

    if (error) throw error
    return data as any[] || []
  },

  async updateOrderStatus(orderId: string, status: Order['order_status'], userId?: string, oldStatus?: string) {
    const updateData: any = { 
      order_status: status,
      updated_at: new Date().toISOString()
    }
    
    if (userId && status === 'new_order_placed') {
      updateData.approved_by = userId
      updateData.approved_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('orders')
      .update(updateData)
      .eq('id', orderId)
      .select()
      .single()

    if (error) throw error

    // Send email notification for status change using edge function
    // Only send emails for specific status transitions
    const emailTriggers = [
      { from: 'results_final', to: 'infusion_ordered' },
      { from: 'infusion_ordered', to: 'infusion_shipped' },
      { from: 'infusion_shipped', to: 'infusion_completed' }
    ]
    
    const shouldSendEmail = emailTriggers.some(trigger => 
      oldStatus === trigger.from && status === trigger.to
    )
    
    if (oldStatus && oldStatus !== status && shouldSendEmail) {
      try {
        await supabase.functions.invoke('send-status-change-email', {
          body: {
            orderId,
            oldStatus,
            newStatus: status
          }
        })
      } catch (emailError) {
        console.error('Error sending status change email:', emailError)
      }
    }
    
    return data
  },

  async createOrder(orderData: any) {
    const { data, error } = await supabase
      .from('orders')
      .insert(orderData)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async updateOrder(orderId: string, orderData: any) {
    // Get the current order to check for status changes
    const { data: currentOrder } = await supabase
      .from('orders')
      .select('order_status')
      .eq('id', orderId)
      .single()

    const { data, error } = await supabase
      .from('orders')
      .update(orderData)
      .eq('id', orderId)
      .select()
      .single()

    if (error) throw error

    // Check if status changed and send email notification
    if (currentOrder && orderData.order_status && currentOrder.order_status !== orderData.order_status) {
      try {
        await supabase.functions.invoke('send-status-change-email', {
          body: {
            orderId,
            oldStatus: currentOrder.order_status,
            newStatus: orderData.order_status
          }
        })
      } catch (emailError) {
        console.error('Error sending status change email:', emailError)
      }
    }

    return data
  },

  async sendStatusChangeEmail(orderId: string, status: Order['order_status']) {
    const emailMap = {
      'new_order_placed': { recipient: 'yogabalaji@gmail.com', subject: 'Order Approved' },
      'new_order_received': { recipient: 'goajailbay@gmail.com', subject: 'Order Received' },
      'results_final': { recipient: 'goajailbay@gmail.com', subject: 'Results Final' },
      'infusion_ordered': { recipient: 'goajailbay@gmail.com', subject: 'Infusion Ordered' },
      'infusion_shipped': { recipient: 'goajailbay@gmail.com', subject: 'Infusion Shipped' },
      'infusion_completed': { recipient: 'goajailbay@gmail.com', subject: 'Infusion Completed' }
    }

    const emailConfig = emailMap[status as keyof typeof emailMap]
    if (emailConfig) {
      await supabase.from('email_log').insert({
        recipient_email: emailConfig.recipient,
        subject: emailConfig.subject,
        body: `Order ${orderId} status changed to ${status}`,
        sent_at: new Date().toISOString()
      })
    }
  },

  // Patients
  async createPatient(patientData: Omit<Patient, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('patients')
      .insert(patientData)
      .select()
      .single()

    if (error) throw error
    return data as Patient
  },

  async getPatientByMRN(mrn: string) {
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .eq('mrn', mrn)
      .maybeSingle()

    if (error) throw error
    return data as Patient | null
  },

  async updatePatient(patientId: string, patientData: Partial<Omit<Patient, 'id' | 'created_at' | 'updated_at'>>) {
    const { data, error } = await supabase
      .from('patients')
      .update({
        ...patientData,
        updated_at: new Date().toISOString()
      })
      .eq('id', patientId)
      .select()
      .single()

    if (error) throw error
    return data as Patient
  },

  // Units
  async updateUnitStatus(unitId: string, status: Unit['unit_status']) {
    const { data, error } = await supabase
      .from('units')
      .update({ 
        unit_status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', unitId)
      .select()
      .single()

    if (error) throw error
    return data as Unit
  }
}